import { LitElement, html, css, t, broadcastReceiver } from '@hh/global.js'

class ProductGalleryModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
  }

  constructor() {
    super()
    this.scrollY = 0
    this.handleCloseButton()
    this.handleThumbButtons()
    this.handleScrollIntersection()
  }

  connectedCallback() {
    super.connectedCallback()

    this.handleLastThumbScroll()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()
  }

  openFromDocument(data) {
    const isGalleryModal = data.id?.includes('product-gallery') ?? false
    if (!isGalleryModal) return
    if (this.id === data.id) {
      this.open = true
      if (document.documentElement.scrollTop > 0) {
        this.scrollY = document.documentElement.scrollTop
      }
      document.body.classList.add('is-open')
    } else {
      this.open = false
      document.body.classList.remove('is-open')
      document.documentElement.scrollTop = this.scrollY
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView({
          behavior: 'smooth',
        })
      }, 100)
    }
  }

  handleCloseButton() {
    this.querySelectorAll('.js-product-gallery-modal-close').forEach((item) => {
      item.addEventListener('click', this.close.bind(this))
    })
  }

  close(event) {
    if (event.type === 'click') {
      this.open = false
      document.body.classList.remove('is-open')
      document.documentElement.scrollTop = this.scrollY
      return
    }
    event.code.toUpperCase() === 'ESCAPE' && (this.open = false)
    document.body.classList.remove('is-open')
    document.documentElement.scrollTop = this.scrollY
  }

  handleThumbButtons() {
    const buttonEls = this.querySelectorAll('.js-product-gallery-thumb-btn')

    buttonEls.forEach((item) => {
      item.addEventListener('click', (e) => {
        const thumbId = e.target.closest('button').dataset?.thumbId

        const mediaEl = this.querySelector(`#${thumbId}`)

        mediaEl?.scrollIntoView({
          behavior: 'smooth',
        })
      })
    })
  }

  handleLastThumbScroll() {
    const thumbList = this.querySelector('.product-gallery-modal__thumb-list')
    const lastThumbButton = this.querySelector(
      '.js-product-gallery-thumb-btn:last-child'
    )

    const intersectionOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === lastThumbButton) {
          if (
            entry.isIntersecting &&
            lastThumbButton.classList.contains('is-active')
          ) {
            thumbList.scrollLeft -= 900 // Adjust the value for the desired scroll amount
          }
        }
      })
    }, intersectionOptions)

    observer.observe(lastThumbButton)
  }

  handleScrollIntersection() {
    const imageEls = this.querySelectorAll('.js-product-gallery-media-item')
    const thumbContainer = this.querySelector(
      '.product-gallery-modal__thumb-list'
    )

    const removeActiveThumb = () => {
      const buttonEls = this.querySelectorAll('.js-product-gallery-thumb-btn')

      buttonEls.forEach((item) => {
        item.classList.remove('is-active')
      })
    }

    const observerConfig = {
      rootMargin: '-32px 0px',
      threshold: 0,
    }

    imageEls.forEach((item, index) => {
      const observer = new IntersectionObserver((elements, self) => {
        elements.forEach((element) => {
          if (element.isIntersecting) {
            const thumbId = element.target?.getAttribute('id')
            const thumbEl = this.querySelector(`[data-thumb-id="${thumbId}"]`)

            removeActiveThumb()
            thumbEl.classList.add('is-active')

            if (index === imageEls.length - 1) {
              thumbContainer.scrollLeft = thumbContainer.scrollWidth
            }
          }
        })
      }, observerConfig)

      observer.observe(item)
    })
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
    >
      <div
        class="content"
        role="document"
        aria-label="${t('products.modal.label')}"
        tabindex="0"
      >
        <hh-focus-trap active="${this.open}">
          <slot></slot>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      background-color: var(--theme-color-reverse);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    .content {
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: auto;
      padding: 0 1rem;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('product-gallery-modal', ProductGalleryModal)
